import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Paper,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Fade,
  Stack,
  Divider,
  alpha,
  useTheme,
  TextField,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Alert,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Favorite as FavoriteIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Agriculture as AgricultureIcon,
  Restaurant as RestaurantIcon,
  LocalDrink as LocalDrinkIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useOrganizationSearch } from '../hooks/useOrganizationSearch';
import { organizationRouter } from '../services/organizationRouter';
import { Farm } from '../../../services/farm/farmService';
import { OrganizationTemplateCard } from './OrganizationTemplateCard';
import { SearchBar } from '../../../components/ui/SearchBar';
import { EmptyState } from '../../../components/ui/EmptyState';
import { 
  OrganizationTemplate,
  CreateOrganizationRequest,
  OrganizationFormData
} from '../types/organizationTypes';
import { Organization } from '../../../types';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

// Import organization-specific form components
import FarmOrganizationForm from '../../farm/components/FarmOrganizationForm';
// MVP: Comentado para o lançamento - habilitar nas versões futuras
// import DairyOrganizationForm from '../../dairy/components/DairyOrganizationForm';
// import SlaughterhouseOrganizationForm from '../../slaughterhouse/components/SlaughterhouseOrganizationForm';
// import CooperativeOrganizationForm from '../../cooperative/components/CooperativeOrganizationForm';

import { useAuthContext } from '../../../contexts/AuthContext';
import { useChatContext } from '../../agent/contexts/ChatContext';
import { chatSessionService } from '../../agent/services/ChatSessionService';

import { CompleteProfileModal } from '../../profile/components/CompleteProfileModal';
import { Tooltip, TooltipProps, tooltipClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { normalizePaidPlanType } from '../../onboarding/services/onboardingService';

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

interface EnhancedNewChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChatCreated: (organization: Farm) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Define a local type to allow `type` to be null during the creation process
type CreationFormData = Omit<OrganizationFormData, 'type'> & {
  type: Organization['type'] | null;
};

// MVP: Apenas fazenda disponível no lançamento
const organizationTypes = [
  { value: 'farm', label: 'Fazenda/Propriedade Rural', icon: AgricultureIcon, color: DESIGN_TOKENS.colors.organization.farm },
  // TODO: Habilitar para versões futuras
  // { value: 'slaughterhouse', label: 'Frigorífico/Abatedouro', icon: RestaurantIcon, color: DESIGN_TOKENS.colors.organization.slaughterhouse },
  // { value: 'dairy', label: 'Laticínio/Usina de Leite', icon: LocalDrinkIcon, color: DESIGN_TOKENS.colors.organization.dairy },
  // { value: 'cooperative', label: 'Cooperativa Agropecuária', icon: GroupsIcon, color: DESIGN_TOKENS.colors.organization.cooperative },
];

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`organization-tabpanel-${index}`}
    aria-labelledby={`organization-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

export const EnhancedNewChatModal: React.FC<EnhancedNewChatModalProps> = ({
  isOpen,
  onClose,
  onChatCreated,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation();
  // Removed local organizations state in favor of context
  const [selectedOrganization, setSelectedOrganization] = useState<Farm | null>(null);
  const [view, setView] = useState<'select' | 'create'>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<OrganizationTemplate | undefined>();
  const [isCreating, setIsCreating] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);
  
  // Profile check state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const { authState: { hasCompleteProfile } } = useAuthContext();

  // UI State for search (Screen Domain)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Farm[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [templateSuggestions, setTemplateSuggestions] = useState<OrganizationTemplate[]>([]);
  const [memberOrganizations, setMemberOrganizations] = useState<Farm[]>([]);
  const [memberLoading, setMemberLoading] = useState(false);

  // Creation form state
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<CreationFormData>({
    name: '',
    type: null,
    description: '',
    tags: [],
    country: 'Brasil',
  });

  const { authState } = useAuthContext();
  const { 
    organizations: contextOrganizations, 
    loadOrganizations: loadChatOrganizations 
  } = useChatContext();
  
  const user = authState.user;
  const ownedTabIndex = 0;
  const memberTabIndex = 1;
  const createTabIndex = 2;
  const userId = authState.profileId || authState.user?.id;
  const planType = normalizePaidPlanType(authState.planType || authState.user?.planType || null);
  const isProfessionalPlan = planType === 'professional';
  const ownedOrganizations = userId
    ? contextOrganizations.filter(org => org.owner_id === userId)
    : contextOrganizations;
  
  // Database operations only (Database Domain)
  const {
    recentOrganizations,
    favoriteOrganizations,
    loading,
    error,
    searchByName,
    loadRecent,
    loadFavorites
  } = useOrganizationSearch();

  // Derived UI state
  const hasSearchQuery = searchQuery.trim().length > 0;

  // UI handlers
  const handleSearchChange = async (query: string) => {
    setSearchQuery(query);
    
    if (query.trim()) {
      setIsSearching(true);
      try {
        const ownerId = authState.profileId || authState.user?.id;
        const results = await searchByName(query, ownerId);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  };

  const loadMemberOrganizations = async () => {
    if (!userId) {
      setMemberOrganizations([]);
      return;
    }

    setMemberLoading(true);

    try {
      const organizations = await organizationRouter.searchByCollaborator(userId);
      setMemberOrganizations(organizations);
    } catch (error) {
      setMemberOrganizations([]);
    } finally {
      setMemberLoading(false);
    }
  };

  // Load initial data
  React.useEffect(() => {
    if (isOpen) {
      loadRecent();
      loadFavorites();
      loadChatOrganizations().catch(console.error);
      loadMemberOrganizations();
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isProfessionalPlan && activeTab === createTabIndex) {
      setActiveTab(ownedTabIndex);
    }
  }, [isProfessionalPlan, activeTab, createTabIndex, ownedTabIndex]);
  
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // clearSearch(); // Don't clear search on tab change, let search persist or handle it differently if needed.
    // Actually, if we remove search tab, search logic is independent.
    // But if we have tabs 0 (Favorites) and 1 (Create), switching tabs shouldn't necessarily clear search unless intended.
    // If I type in search, it overrides view. If I switch tab, maybe I want to see that tab.
    // Let's keep clearSearch for now if user explicitly clicks a tab, it implies leaving search context?
    // But if search tab is gone, search is an overlay.
    if (hasSearchQuery) {
        clearSearch();
    }
  };

  const handleOrganizationSelect = async (organization: Farm) => {
    // Call the provided callback to handle session creation
    onChatCreated(organization);
    
    // Simply close modal - let user navigate to chat manually
    handleClose();
  };

  const handleTemplateSelect = (template: OrganizationTemplate) => {
    setSelectedTemplate(template);
    
    // Initialize form data with template
    const { type: _, ...safeDefaultData } = template.defaultFormData || {};
    setFormData({
      name: '',
      type: template.type,
      description: template.description || '',
      tags: template.suggestedTags || [],
      country: 'Brasil',
      ...safeDefaultData,
    });
    
    // Set initial step based on whether we have a template
    setActiveStep(0);
    setView('create');
  };

  const handleCreateOrganization = async (formData: CreationFormData) => {
    try {
      setIsCreating(true);
      setCreationError(null);
      
      // Ensure formData.type is not null before creating organization
      if (!formData.type) {
        throw new Error('Tipo de organização é obrigatório');
      }
      
      // Add owner_id to formData - Align with ChatContext loadOrganizations logic to ensure consistency
      const formDataWithOwner = {
        ...formData,
        owner_id: authState.profileId || authState.user?.id
      };
      
      const createResult = await organizationRouter.create(formData.type, formDataWithOwner);
        
        // Extract the _key from the API response
        const createdKey = (createResult as any)?.data?.result?._key || (createResult as any)?._key;
  
        
        if (!createdKey) {
          console.error('❌ [DEBUG] Falha ao extrair key da resposta');
          throw new Error('Falha ao obter ID da organização criada');
        }
        
  
        
        // Fechar modal imediatamente após criação bem-sucedida
        
        handleClose();
        
        // Atualizar listas em background
        
        loadChatOrganizations().catch(console.error);
        // loadOrganizations().catch(console.error);
        
        
         return; // Sair da função aqui
    } catch (error: any) {
      console.error('Error creating organization:', error);
      setCreationError(error.message || 'Erro ao criar organização. Tente novamente.');
    } finally {
      setIsCreating(false);
    }
  };

  // Removed local loadOrganizations definition

  const handleClose = () => {
    setSelectedOrganization(null);
    setActiveTab(ownedTabIndex);
    setView('select');
    setSelectedTemplate(undefined);
    setActiveStep(0);
    setFormData({
      name: '',
      type: null,
      description: '',
      tags: [],
      country: 'Brasil',
    });
    setCreationError(null);
    clearSearch();
    onClose();
  };

  // Creation form handlers
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setCreationError(null);
    if (activeStep === 0 || (selectedTemplate && activeStep === 1)) {
      setView('select');
      setActiveStep(0);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleInputChange = (field: keyof CreationFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleTypeChange = (type: Organization['type']) => {
    setFormData(prev => ({ ...prev, type }));
  };

  const handleOrganizationSpecificSubmit = async (data: any) => {

    
    await handleCreateOrganization(data as OrganizationFormData);
  };

  const isStepValid = (step: number): boolean => {
    const currentActualStep = selectedTemplate ? step + 1 : step;
    
    switch (currentActualStep) {
      case 0: // Type selection
        return formData.type !== null;
      case 1: // Specific form
        return true; 
      default:
        return false;
    }
  };

  const actualStep = selectedTemplate ? activeStep + 1 : activeStep;
  const steps = selectedTemplate ? 
    ['Detalhes da Fazenda'] : 
    ['Tipo de Organização', 'Detalhes da Fazenda'];

  // Get dialog title based on current view and step
  const getDialogTitle = () => {
    if (view === 'select') {
      return {
        main: 'Nova Conversa',
        sub: 'Selecione uma organização ou crie uma nova'
      };
    }

    const orgTypeInfo = organizationTypes.find((t) => t.value === formData.type);
    const orgLabel = orgTypeInfo?.label || 'Organização';

    switch (actualStep) {
      case 0:
        return { main: 'Selecione o Tipo de Organização', sub: '' };
      case 1:
        return { main: `Criar ${orgLabel}`, sub: 'Preencha os dados da fazenda' };
      default:
        return { main: 'Nova Organização', sub: '' };
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 24
      }
    }
  };

  const renderOrganizationList = (organizations: Farm[], options?: { showCreateCard?: boolean }) => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        marginTop: 16,
      }}
    >
      {options?.showCreateCard !== false && (
        <motion.div variants={itemVariants}>
            <HtmlTooltip
              title={
                !hasCompleteProfile ? (
                  <Box>
                    <Typography color="inherit" variant="subtitle2" fontWeight="bold">
                      Completar Perfil
                    </Typography>
                    <Typography variant="caption" display="block" paragraph>
                      Para criar uma nova fazenda, precisamos que você complete seu cadastro primeiro.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowProfileModal(true);
                      }}
                    >
                      Completar Agora
                    </Button>
                  </Box>
                ) : (
                  ''
                )
              }
              disableHoverListener={hasCompleteProfile}
              disableFocusListener={hasCompleteProfile}
              disableTouchListener={hasCompleteProfile}
              arrow
              placement="top"
            >
              <Paper
                variant="outlined"
                sx={{
                  borderColor: alpha(DESIGN_TOKENS.colors.organization.farm, 0.5),
                  bgcolor: alpha(DESIGN_TOKENS.colors.organization.farm, 0.05),
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: DESIGN_TOKENS.colors.organization.farm,
                    bgcolor: alpha(DESIGN_TOKENS.colors.organization.farm, 0.1),
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
              >
                <ListItemButton
                  disabled={!hasCompleteProfile}
                  onClick={() => {
                    if (!hasCompleteProfile) return;

                    setFormData({
                      name: '',
                      type: 'farm',
                      description: '',
                      tags: [],
                      country: 'Brasil',
                    });
                    setActiveStep(1);
                    setView('create');
                  }}
                  sx={{ py: 2 }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: DESIGN_TOKENS.colors.organization.farm }}>
                      <AddIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Criar nova fazenda"
                    primaryTypographyProps={{
                      variant: 'subtitle1',
                      fontWeight: 600,
                      color: DESIGN_TOKENS.colors.organization.farm,
                    }}
                    secondary="Cadastre uma nova propriedade rural"
                  />
                </ListItemButton>
              </Paper>
            </HtmlTooltip>
        </motion.div>
      )}

      {organizations.length > 0 && (
        <motion.div variants={itemVariants}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ ml: 1, mb: 1, display: 'block' }}
          >
            Minhas Fazendas
          </Typography>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <List disablePadding>
              {organizations.map((org) => (
                <ListItem
                  key={org.id || (org as any)._key || org.name}
                  disablePadding
                  divider
                >
                  <ListItemButton
                    onClick={() => handleOrganizationSelect(org)}
                    sx={{ py: 1.5 }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha(DESIGN_TOKENS.colors.organization.farm, 0.1),
                          color: DESIGN_TOKENS.colors.organization.farm,
                        }}
                      >
                        <AgricultureIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={org.name}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </motion.div>
      )}
    </motion.div>
  );

  const renderTemplateGrid = (templates: OrganizationTemplate[]) => (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 16,
        marginTop: 16,
      }}
    >
      {templates.map((template) => (
        <motion.div key={template.id} variants={itemVariants}>
          <Box>
            <OrganizationTemplateCard
              template={template}
              onSelect={handleTemplateSelect}
              variant="compact"
            />
          </Box>
        </motion.div>
      ))}
    </motion.div>
  );

  const renderCreateStepContent = () => {
    switch (actualStep) {
      case 0:
        return (
          <Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
              {organizationTypes.map((type) => {
                const IconComponent = type.icon;
                const isSelected = formData.type === type.value;
                return (
                  <Box
                    key={type.value}
                    onClick={() => handleTypeChange(type.value as Organization['type'])}
                    sx={{
                      p: 2,
                      border: `2px solid ${isSelected ? type.color : alpha(type.color, 0.2)}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      backgroundColor: isSelected ? alpha(type.color, 0.1) : 'transparent',
                      '&:hover': {
                        borderColor: type.color,
                        backgroundColor: alpha(type.color, 0.05),
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: alpha(type.color, 0.2), color: type.color, mr: 2 }}>
                        <IconComponent />
                      </Avatar>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {type.label}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 1:
        switch (formData.type) {
          case 'farm':
            return <FarmOrganizationForm initialData={formData as OrganizationFormData} onSubmit={handleOrganizationSpecificSubmit} onBack={handleBack} isLoading={isCreating} />;
          // MVP: Comentado para o lançamento - habilitar nas versões futuras
          // case 'dairy':
          //   return <DairyOrganizationForm initialData={formData as OrganizationFormData} onSubmit={handleOrganizationSpecificSubmit} onBack={handleBack} isLoading={isCreating} />;
          // case 'slaughterhouse':
          //   return <SlaughterhouseOrganizationForm initialData={formData as OrganizationFormData} onSubmit={handleOrganizationSpecificSubmit} onBack={handleBack} isLoading={isCreating} />;
          // case 'cooperative':
          //   return <CooperativeOrganizationForm initialData={formData as OrganizationFormData} onSubmit={handleOrganizationSpecificSubmit} onBack={handleBack} isLoading={isCreating} />;
          default:
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">
                  Tipo de organização inválido. Por favor, volte e selecione um tipo.
                </Typography>
              </Box>
            );
        }

      default:
        return null;
    }
  };

  const dialogTitle = getDialogTitle();
  const showStepper = view === 'create' && actualStep !== 1;
  const showDialogActions = view === 'create' && actualStep !== 1;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth={view === 'create' ? 'md' : 'lg'}
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            minHeight: isMobile ? '100%' : (view === 'create' ? (actualStep === 2 ? '80vh' : 'auto') : '70vh'),
            maxHeight: isMobile ? '100%' : '90vh',
            m: isMobile ? 0 : 32
          }
        }}
      >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: view === 'select' 
            ? `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[800]} 0%, ${DESIGN_TOKENS.colors.organization.farm} 100%)`
            : 'transparent',
          color: view === 'select' ? 'white' : 'inherit',
          pb: view === 'select' ? 2 : 1,
          pt: isMobile ? 2 : 2,
          px: isMobile ? 2 : 3,
        }}
      >
        <Box>
          <Typography variant={view === 'select' ? 'h5' : 'h6'} sx={{ fontWeight: 600, mb: dialogTitle.sub ? 0.5 : 0, color: view === 'select' ? 'white' : 'inherit' }}>
            {dialogTitle.main}
          </Typography>
          {dialogTitle.sub && (
            <Typography variant="body2" sx={{ opacity: view === 'select' ? 0.9 : 0.7, color: view === 'select' ? 'white' : 'inherit' }}>
              {dialogTitle.sub}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} sx={{ color: view === 'select' ? 'white' : 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, pb: actualStep === 2 ? 0 : (isMobile ? 2 : 3) }}>
        <AnimatePresence mode="wait">
          {view === 'select' ? (
            <motion.div
              key="select"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Search Bar */}
              <Box sx={{ p: isMobile ? 2 : 3, pb: 0 }}>
                <SearchBar
                  value={searchQuery}
                  onChange={(value) => {
                    handleSearchChange(value);
                  }}
                  onClear={() => {
                    clearSearch();
                  }}
                  placeholder={t('chat.newChat.searchPlaceholder')}
                  isLoading={isSearching}
                  autoFocus
                />
              </Box>

              {/* Tabs */}
              <Box sx={{ px: isMobile ? 2 : 3, pt: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant={isMobile ? "scrollable" : "fullWidth"}
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 'medium',
                      minHeight: 48,
                      fontSize: isMobile ? '0.875rem' : '1rem',
                    },
                  }}
                >
                  <Tab
                    icon={<AgricultureIcon />}
                    label="Minhas Fazendas"
                    value={ownedTabIndex}
                    iconPosition={isMobile ? "top" : "start"}
                  />
                  <Tab
                    icon={<GroupsIcon />}
                    label={t('chat.newChat.memberFarmsTab')}
                    value={memberTabIndex}
                    iconPosition={isMobile ? "top" : "start"}
                  />
                  {!isProfessionalPlan && (
                    <Tab
                      icon={<AddIcon />}
                      label="Criar Nova"
                      value={createTabIndex}
                      iconPosition={isMobile ? "top" : "start"}
                    />
                  )}
                </Tabs>
              </Box>

              <Divider />

              {/* Tab Content */}
              <Box sx={{ p: isMobile ? 2 : 3, minHeight: 400 }}>
                {/* Global Search Results Overlay */}
                {hasSearchQuery ? (
                    <>
                      {/* Organization Results */}
                      {searchResults.length > 0 && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            Suas Organizações ({searchResults.length})
                          </Typography>
                          {renderOrganizationList(searchResults, { showCreateCard: !isProfessionalPlan })}
                        </>
                      )}

                      {/* No results */}
                      {searchResults.length === 0 && (
                        isProfessionalPlan ? (
                          <EmptyState
                            icon={SearchIcon}
                            title="Nenhum resultado encontrado"
                            description={`Não encontramos organizações para "${searchQuery}".`}
                            size="medium"
                          />
                        ) : (
                          <EmptyState
                            icon={SearchIcon}
                            title="Nenhum resultado encontrado"
                            description={`Não encontramos organizações para "${searchQuery}". Tente buscar por outro termo ou crie uma nova organização.`}
                            actionLabel="Criar Nova Organização"
                            onAction={() => {
                              clearSearch();
                              setActiveTab(createTabIndex);
                            }}
                            size="medium"
                          />
                        )
                      )}
                    </>
                ) : (
                  <>
                    <TabPanel value={activeTab} index={ownedTabIndex}>
                      {loading ? (
                        <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                          Carregando fazendas...
                        </Typography>
                      ) : ownedOrganizations.length > 0 ? (
                        <>
                          {renderOrganizationList(ownedOrganizations, { showCreateCard: !isProfessionalPlan })}
                        </>
                      ) : (
                        isProfessionalPlan ? (
                          <EmptyState
                            icon={AgricultureIcon}
                            title="Nenhuma fazenda cadastrada"
                            description="Você ainda não possui fazendas cadastradas."
                            size="medium"
                          />
                        ) : (
                          <EmptyState
                            icon={AgricultureIcon}
                            title="Nenhuma fazenda cadastrada"
                            description="Crie sua primeira fazenda para começar a usar a IA especializada."
                            actionLabel="Criar Fazenda"
                            onAction={() => setActiveTab(createTabIndex)}
                            size="medium"
                          />
                        )
                      )}
                    </TabPanel>

                    <TabPanel value={activeTab} index={memberTabIndex}>
                      {memberLoading ? (
                        <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                          {t('chat.newChat.loadingMemberFarms')}
                        </Typography>
                      ) : memberOrganizations.length > 0 ? (
                        <>
                          {renderOrganizationList(memberOrganizations, { showCreateCard: !isProfessionalPlan })}
                        </>
                      ) : (
                        isProfessionalPlan ? (
                          <EmptyState
                            icon={GroupsIcon}
                            title={t('chat.newChat.memberFarmsEmptyTitle')}
                            description={t('chat.newChat.memberFarmsEmptyDescription')}
                            size="medium"
                          />
                        ) : (
                          <EmptyState
                            icon={GroupsIcon}
                            title={t('chat.newChat.memberFarmsEmptyTitle')}
                            description={t('chat.newChat.memberFarmsEmptyDescription')}
                            actionLabel={t('chat.newChat.memberFarmsEmptyAction')}
                            onAction={() => setActiveTab(createTabIndex)}
                            size="medium"
                          />
                        )
                      )}
                    </TabPanel>

                    {!isProfessionalPlan && (
                        <TabPanel value={activeTab} index={createTabIndex}>
                          <Stack spacing={3}>
                            <Box>
                              <Typography variant="h6" gutterBottom>
                                Tipos de Organização
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Escolha um modelo para começar rapidamente
                              </Typography>
                              
                              <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2, mt: 2 }}>
                                {organizationTypes.map((type) => {
                                  const IconComponent = type.icon;
                                  return (
                                    <Box
                                      key={type.value}
                                      onClick={() => {
                                        setFormData({
                                          name: '',
                                          type: type.value as Organization['type'],
                                          description: '',
                                          tags: [],
                                          country: 'Brasil',
                                        });
                                        setActiveStep(1);
                                        setView('create');
                                      }}
                                      sx={{
                                        p: 3,
                                        border: `1px solid ${alpha(type.color, 0.3)}`,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        backgroundColor: alpha(type.color, 0.05),
                                        '&:hover': {
                                          borderColor: type.color,
                                          backgroundColor: alpha(type.color, 0.1),
                                          transform: 'translateY(-2px)',
                                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        },
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Avatar sx={{ bgcolor: alpha(type.color, 0.2), color: type.color, mr: 2, width: 48, height: 48 }}>
                                        <IconComponent fontSize="medium" />
                                      </Avatar>
                                      <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                          {type.label}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          Criar nova {type.label.split('/')[0]}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Stack>
                        </TabPanel>
                      )}

                  </>
                )}
              </Box>
            </motion.div>
          ) : (
            <motion.div
              key="create"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              {/* Creation Form Content */}
              {showStepper && (
                <Box sx={{ mb: 4 }}>
                  <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                      <Step key={label}><StepLabel>{label}</StepLabel></Step>
                    ))}
                  </Stepper>
                </Box>
              )}

              {renderCreateStepContent()}
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>

      {view === 'select' ? (
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
          <Button onClick={handleClose} color="inherit">
            Cancelar
          </Button>
          <Box sx={{ flexGrow: 1 }} />
        </DialogActions>
      ) : (
        showDialogActions && (
          <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', alignItems: 'stretch' }}>
            {creationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {creationError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
              >
                Voltar
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                onClick={handleNext}
                variant="contained"
                disabled={!isStepValid(activeStep) || isCreating}
                endIcon={<ArrowForwardIcon />}
              >
                Próximo
              </Button>
            </Box>
          </DialogActions>
                 )
       )}

      {/* Complete Profile Modal */}
      <CompleteProfileModal 
        open={showProfileModal} 
        onClose={() => setShowProfileModal(false)}
        onSuccess={() => setShowProfileModal(false)}
      />
     </Dialog>

   </>
   );
 };
